/**
 * Global Feed Alpine.js Component
 *
 * Manages the global feed page with three tabs:
 * - Status Updates (server-side rendered via /app/helpers/live)
 * - Posts (server-side rendered via /app/helpers/global-feed-posts)
 * - Friends Feed (server-side rendered via /app/helpers/friends-feed-posts)
 *
 * Features:
 * - URL query param sync for deep linking
 * - Infinite scroll pagination for all tabs
 * - New posts indicator (polling)
 * - Responsive filters (dropdown on desktop, bottom sheet on mobile)
 * - Lazy Leaflet map initialization for status cards
 */

const POLL_INTERVAL_MS = 30000; // 30 seconds
const ITEMS_PER_PAGE = 10;
const STATUS_ITEMS_PER_PAGE = 12;

export function globalFeedComponent() {
    return {
        // Tab state
        activeTab: 'status',

        // Filter state
        filters: {
            countries: [],
            languages: [],
            postType: null,
        },

        // HTML content per tab (server-rendered)
        statusHtml: '',
        postsHtml: '',
        friendsHtml: '',

        // Pagination cursors (stored as strings to handle large IDs)
        cursors: {
            posts: null,
            friends: null,
            status: null,
        },

        // Loading states
        isLoading: false,
        isLoadingMore: false,
        hasMore: {
            posts: true,
            friends: true,
            status: true,
        },

        // New posts indicator
        newPostsCount: 0,
        pollingInterval: null,

        // Map observer for lazy initialization
        _mapObserver: null,

        // Error state
        error: null,

        /**
         * Initialize the component
         */
        async init(initialTab, initialFilters) {
            this.activeTab = initialTab || 'status';

            if (initialFilters) {
                this.filters.countries = initialFilters.countries || [];
                this.filters.languages = initialFilters.languages || [];
                this.filters.postType = initialFilters.postType || null;
            }

            // Read from URL params on page load
            this.readFromURL();

            // Handle browser back/forward navigation
            window.addEventListener('popstate', () => {
                this.readFromURL();
                this.loadCurrentTab();
            });

            // Handle visibility change for polling
            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    this.stopPolling();
                } else {
                    this.startPolling();
                }
            });

            // Set up IntersectionObserver for lazy map initialization
            this._setupMapObserver();

            // Load initial data
            await this.loadCurrentTab();

            // Start polling for new posts
            this.startPolling();
        },

        /**
         * Read state from URL query parameters
         */
        readFromURL() {
            const params = new URLSearchParams(window.location.search);

            const tab = params.get('tab');
            if (tab && ['status', 'posts', 'friends'].includes(tab)) {
                this.activeTab = tab;
            }

            const countries = params.get('countries');
            this.filters.countries = countries ? countries.split(',').filter(Boolean) : [];

            const languages = params.get('languages');
            this.filters.languages = languages ? languages.split(',').filter(Boolean) : [];

            const postType = params.get('post_type');
            this.filters.postType = postType || null;
        },

        /**
         * Update URL with current state
         */
        updateURL() {
            const params = new URLSearchParams();

            if (this.activeTab !== 'status') {
                params.set('tab', this.activeTab);
            }

            if (this.filters.countries.length) {
                params.set('countries', this.filters.countries.join(','));
            }

            if (this.filters.languages.length) {
                params.set('languages', this.filters.languages.join(','));
            }

            if (this.filters.postType) {
                params.set('post_type', this.filters.postType);
            }

            const queryString = params.toString();
            const newUrl = queryString
                ? `${window.location.pathname}?${queryString}`
                : window.location.pathname;

            window.history.pushState({}, '', newUrl);
        },

        /**
         * Switch to a different tab
         */
        async switchTab(tab) {
            if (this.activeTab === tab) return;

            this.activeTab = tab;
            this.newPostsCount = 0;
            this.updateURL();

            await this.loadCurrentTab();

            if (tab === 'status' && this.statusHtml) {
                this.$nextTick(() => {
                    this.initStatusMaps();
                });
            }

            this.startPolling();
        },

        /**
         * Load data for the current tab
         */
        async loadCurrentTab() {
            this.error = null;

            if (this.activeTab === 'status') {
                if (!this.statusHtml) {
                    this.hasMore.status = true;
                    await this.loadStatusUpdates(false);
                }
            } else if (this.activeTab === 'posts') {
                if (!this.postsHtml) {
                    this.hasMore.posts = true;
                    await this.loadPosts(false);
                }
            } else if (this.activeTab === 'friends') {
                if (!this.friendsHtml) {
                    this.hasMore.friends = true;
                    await this.loadFriendsPosts(false);
                }
            }
        },

        /**
         * Load status updates via server-side endpoint with cursor pagination
         */
        async loadStatusUpdates(append = false) {
            if (append) {
                this.isLoadingMore = true;
            } else {
                this.isLoading = true;
                this.statusHtml = '';
                this.cursors.status = null;
            }
            this.error = null;

            try {
                const params = new URLSearchParams({
                    limit: String(STATUS_ITEMS_PER_PAGE),
                });

                if (append && this.cursors.status) {
                    params.set('cursor', this.cursors.status);
                }

                const response = await fetch(`/app/helpers/live?${params}`);
                if (!response.ok) {
                    throw new Error('Failed to load status updates');
                }

                const html = await response.text();

                // Parse cursor and hasMore from the response
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;
                const container = tempDiv.querySelector('[data-next-cursor]');

                if (container) {
                    this.cursors.status = container.dataset.nextCursor || null;
                    this.hasMore.status = container.dataset.hasMore === 'true';
                }

                if (append) {
                    const existingContainer = document.querySelector('#liveStats [data-next-cursor]');
                    if (existingContainer && container) {
                        // Append new card elements to existing grid
                        const newCards = container.children;
                        while (newCards.length > 0) {
                            existingContainer.appendChild(newCards[0]);
                        }
                        existingContainer.dataset.nextCursor = this.cursors.status;
                        existingContainer.dataset.hasMore = this.hasMore.status;
                    }
                } else {
                    this.statusHtml = html;
                }

                // Initialize icons and maps for new content
                this.$nextTick(() => {
                    window.LucideIcons?.initialize(document.querySelector('#liveStats'));
                    this.initStatusMaps();
                });

            } catch (err) {
                console.error('Error loading status updates:', err);
                this.error = 'Failed to load status updates. Please try again.';
                this.hasMore.status = false;
            } finally {
                this.isLoading = false;
                this.isLoadingMore = false;
            }
        },

        /**
         * Load global posts via server-side endpoint
         */
        async loadPosts(append = false) {
            if (append) {
                this.isLoadingMore = true;
            } else {
                this.isLoading = true;
                this.postsHtml = '';
                this.cursors.posts = null;
            }
            this.error = null;

            try {
                const params = new URLSearchParams({
                    limit: String(ITEMS_PER_PAGE),
                });

                if (append && this.cursors.posts) {
                    params.set('cursor', this.cursors.posts);
                }

                if (this.filters.countries.length) {
                    params.set('countries', this.filters.countries.join(','));
                }
                if (this.filters.languages.length) {
                    params.set('languages', this.filters.languages.join(','));
                }
                if (this.filters.postType) {
                    params.set('post_type', this.filters.postType);
                }

                const response = await fetch(`/app/helpers/global-feed-posts?${params}`);
                if (!response.ok) {
                    throw new Error('Failed to load posts');
                }

                const html = await response.text();

                // Parse cursor and hasMore from the response
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;
                const container = tempDiv.querySelector('[data-next-cursor]');

                if (container) {
                    this.cursors.posts = container.dataset.nextCursor || null;
                    this.hasMore.posts = container.dataset.hasMore === 'true';
                }

                if (append) {
                    // Append new posts to existing HTML
                    const existingContainer = document.querySelector('#posts-container');
                    if (existingContainer && container) {
                        existingContainer.insertAdjacentHTML('beforeend', container.innerHTML);
                        existingContainer.dataset.nextCursor = this.cursors.posts;
                        existingContainer.dataset.hasMore = this.hasMore.posts;
                    }
                } else {
                    this.postsHtml = html;
                }

            } catch (err) {
                console.error('Error loading posts:', err);
                this.error = 'Failed to load posts. Please try again.';
                this.hasMore.posts = false;
            } finally {
                this.isLoading = false;
                this.isLoadingMore = false;
            }
        },

        /**
         * Load friends posts via server-side endpoint
         */
        async loadFriendsPosts(append = false) {
            if (append) {
                this.isLoadingMore = true;
            } else {
                this.isLoading = true;
                this.friendsHtml = '';
                this.cursors.friends = null;
            }
            this.error = null;

            try {
                const params = new URLSearchParams({
                    limit: String(ITEMS_PER_PAGE),
                });

                if (append && this.cursors.friends) {
                    params.set('cursor', this.cursors.friends);
                }

                const response = await fetch(`/app/helpers/friends-feed-posts?${params}`);
                if (!response.ok) {
                    throw new Error('Failed to load friends posts');
                }

                const html = await response.text();

                // Parse cursor and hasMore from the response
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;
                const container = tempDiv.querySelector('[data-next-cursor]');

                if (container) {
                    this.cursors.friends = container.dataset.nextCursor || null;
                    this.hasMore.friends = container.dataset.hasMore === 'true';
                }

                if (append) {
                    // Append new posts to existing HTML
                    const existingContainer = document.querySelector('#friends-container');
                    if (existingContainer && container) {
                        existingContainer.insertAdjacentHTML('beforeend', container.innerHTML);
                        existingContainer.dataset.nextCursor = this.cursors.friends;
                        existingContainer.dataset.hasMore = this.hasMore.friends;
                    }
                } else {
                    this.friendsHtml = html;
                }

            } catch (err) {
                console.error('Error loading friends posts:', err);
                this.error = 'Failed to load friends posts. Please try again.';
                this.hasMore.friends = false;
            } finally {
                this.isLoading = false;
                this.isLoadingMore = false;
            }
        },

        /**
         * Load more items (infinite scroll)
         */
        async loadMore() {
            if (this.isLoading || this.isLoadingMore || this.error) return;

            if (this.activeTab === 'status' && this.hasMore.status) {
                await this.loadStatusUpdates(true);
            } else if (this.activeTab === 'posts' && this.hasMore.posts) {
                await this.loadPosts(true);
            } else if (this.activeTab === 'friends' && this.hasMore.friends) {
                await this.loadFriendsPosts(true);
            }
        },

        /**
         * Set up IntersectionObserver for lazy map initialization
         */
        _setupMapObserver() {
            if (typeof IntersectionObserver === 'undefined') return;

            this._mapObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this._createMap(entry.target);
                        this._mapObserver.unobserve(entry.target);
                    }
                });
            }, {
                rootMargin: '200px',
            });
        },

        /**
         * Observe uninitialized map elements in the status grid
         */
        initStatusMaps() {
            if (!this._mapObserver) return;

            const maps = document.querySelectorAll('[id^="status-map-"]:not([data-map-init])');
            maps.forEach(mapEl => {
                this._mapObserver.observe(mapEl);
            });
        },

        /**
         * Create a read-only Leaflet map on an element
         */
        _createMap(mapEl) {
            if (typeof L === 'undefined' || mapEl.dataset.mapInit) return;

            const lat = parseFloat(mapEl.dataset.lat);
            const lng = parseFloat(mapEl.dataset.lng);
            if (isNaN(lat) || isNaN(lng)) return;

            const map = L.map(mapEl, {
                zoomControl: false,
                attributionControl: false,
                dragging: false,
                touchZoom: false,
                scrollWheelZoom: false,
                doubleClickZoom: false,
                boxZoom: false,
                keyboard: false,
            }).setView([lat, lng], 3);

            const tileUrl = window.__mapTileUrl || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
            L.tileLayer(tileUrl, { opacity: 0.9 }).addTo(map);

            L.circleMarker([lat, lng], {
                radius: 5,
                fillColor: '#0ea5e9',
                fillOpacity: 0.9,
                color: '#fff',
                weight: 2,
            }).addTo(map);

            mapEl.dataset.mapInit = 'true';
        },

        /**
         * Start polling for new posts
         */
        startPolling() {
            this.stopPolling();

            if (this.activeTab === 'status') {
                // No periodic full re-fetch for status; rely on infinite scroll
                return;
            }

            // For posts/friends, check for new items
            this.pollingInterval = setInterval(() => {
                if (!document.hidden) {
                    this.checkNewPosts();
                }
            }, POLL_INTERVAL_MS);
        },

        /**
         * Stop polling
         */
        stopPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }
        },

        /**
         * Check for new posts since last load
         */
        async checkNewPosts() {
            if (this.activeTab === 'status') return;

            try {
                const endpoint = this.activeTab === 'posts'
                    ? '/app/helpers/global-feed-posts'
                    : '/app/helpers/friends-feed-posts';

                const params = new URLSearchParams({ limit: '1' });
                if (this.activeTab === 'posts') {
                    if (this.filters.countries.length) {
                        params.set('countries', this.filters.countries.join(','));
                    }
                    if (this.filters.languages.length) {
                        params.set('languages', this.filters.languages.join(','));
                    }
                    if (this.filters.postType) {
                        params.set('post_type', this.filters.postType);
                    }
                }

                const response = await fetch(`${endpoint}?${params}`);
                if (!response.ok) return;

                const html = await response.text();
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;
                const container = tempDiv.querySelector('[data-next-cursor]');

                if (container) {
                    const newCursor = container.dataset.nextCursor;
                    const currentCursor = this.activeTab === 'posts'
                        ? this.cursors.posts
                        : this.cursors.friends;

                    // If the newest post ID is different, there are new posts
                    if (newCursor && currentCursor && newCursor !== currentCursor) {
                        // Simplified: just show "new posts available" without counting
                        this.newPostsCount = 1;
                    }
                }
            } catch (err) {
                console.error('Error checking for new posts:', err);
            }
        },

        /**
         * Show new posts by reloading the feed
         */
        showNewPosts() {
            this.newPostsCount = 0;

            if (this.activeTab === 'posts') {
                this.postsHtml = '';
                this.loadPosts(false);
            } else if (this.activeTab === 'friends') {
                this.friendsHtml = '';
                this.loadFriendsPosts(false);
            }

            // Scroll to top smoothly
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },

        /**
         * Apply filters and reload
         */
        async applyFilters() {
            this.updateURL();

            // Reset and reload data for current tab
            if (this.activeTab === 'posts') {
                this.postsHtml = '';
                await this.loadPosts(false);
            } else if (this.activeTab === 'friends') {
                this.friendsHtml = '';
                await this.loadFriendsPosts(false);
            }
        },

        /**
         * Clear all filters
         */
        async clearFilters() {
            this.filters.countries = [];
            this.filters.languages = [];
            this.filters.postType = null;
            window.dispatchEvent(new CustomEvent('clear-feed-filters'));
            await this.applyFilters();
        },

        /**
         * Check if any filters are active
         */
        get hasActiveFilters() {
            return this.filters.countries.length > 0 ||
                   this.filters.languages.length > 0 ||
                   this.filters.postType !== null;
        },

        /**
         * Check if current tab has more items to load
         */
        get currentTabHasMore() {
            if (this.activeTab === 'status') return this.hasMore.status;
            if (this.activeTab === 'posts') return this.hasMore.posts;
            if (this.activeTab === 'friends') return this.hasMore.friends;
            return false;
        },

        /**
         * Check if current tab has content
         */
        get currentTabHasContent() {
            if (this.activeTab === 'status') return !!this.statusHtml;
            if (this.activeTab === 'posts') return !!this.postsHtml;
            if (this.activeTab === 'friends') return !!this.friendsHtml;
            return false;
        },

        /**
         * Get current tab data for template checks
         * Returns an array-like object with length property for template conditions
         */
        get currentTabData() {
            if (this.activeTab === 'status') return this.statusHtml ? [1] : [];
            if (this.activeTab === 'posts') return this.postsHtml ? [1] : [];
            if (this.activeTab === 'friends') return this.friendsHtml ? [1] : [];
            return [];
        },

        /**
         * Cleanup on component destroy
         */
        destroy() {
            this.stopPolling();
            if (this._mapObserver) {
                this._mapObserver.disconnect();
                this._mapObserver = null;
            }
            window.removeEventListener('popstate', this.readFromURL);
        },
    };
}
